import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BsPencilFill, BsSearch, BsStarFill, BsXLg } from "react-icons/bs";
import Select from "../SelectWithValues/SelectWithValues";
import ModalExcluirItem from "../ModalExclusão";
import api from "../../utils/axios/api";
import styles from "./EntrepreneurAccountProducts.module.css";
import { Produto } from "../CardProduto/CardProduto";
import { Link } from "react-router-dom";
import noImage from '../../assets/no-image.jpg'

type FilterOptions = 'higherPrices' | 'lowestPrices' | 'higherQuantities' | 'lowestQuantities';

const EntrepreneurAccountProducts = () => {
  const { t } = useTranslation();

  const [products, setProducts] = useState<Produto[]>([]);
  const [filter, setFilter] = useState<FilterOptions>('higherPrices');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Produto | null>(null);
  const storeId = localStorage.getItem("storeId")

  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window?.innerWidth < 1000
  );

  const navigate = useNavigate();

  const handleFilter = (data: Produto[]) => {
    if (filter === 'higherPrices') {
      data.sort((a, b) => b.originalPrice - a.originalPrice);
    } else if (filter === 'lowestPrices') {
      data.sort((a, b) => a.originalPrice - b.originalPrice);
    } else if (filter === 'higherQuantities') {
      data.sort((a, b) => b.quantity - a.quantity);
    } else if (filter === 'lowestQuantities') {
      data.sort((a, b) => a.quantity - b.quantity);
    }

    return data;
  };

  const handleRemoveProduct = async (id: string) => {
    setSelectedProduct(products.find(product => product.id === id) || null);
    setIsModalOpen(true);
  };

  const handleExcluir = async (id: string) => {
    try {
      await api.delete(`/product/${id}`);
      setProducts(products.filter(product => product.id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    async function loadProducts() {
      const token = localStorage.getItem('token');
      if (!token) {
        return navigate('/login');
      }
      const userId = localStorage.getItem("userId");
      const response = await api.get(`/product/entrepeneur/${userId}`);
      const sortedProducts = handleFilter([...response.data]);
      setProducts(sortedProducts);
    }

    loadProducts();
  }, []);

  useEffect(() => {
    const sortedProducts = handleFilter([...products]);
    setProducts(sortedProducts);
  }, [filter]);

  useEffect(() => {
    function handleResize() {
      setIsSmallScreen(window.innerWidth < 1000);
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.mainContainer}>
      <h1>{t('entrepeneurAccountProducts.title')}</h1>

      {!storeId ? <Link to={"/auth/entrepreneur/account/data"} style={{ marginTop: 8 }}>Antes de gerenciar algum produto, é necessário criar uma loja</Link>
      :
        <div>
          <div className={styles.headerContainer}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                placeholder="Busque um produto"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <BsSearch />
            </div>
            <button type="button" onClick={() => navigate('/auth/entrepreneur/account/products/add')} className={styles.hideOnSmall}>
              {t('entrepeneurAccountProducts.addProduct')}
            </button>
          </div>
          <div className={styles.buttonResponsive}>
            <div className={styles.botao}>
              <button type="button" onClick={() => navigate('/auth/entrepreneur/account/products/add')} className={styles.hideOnSmall}>
                {t('entrepeneurAccountProducts.addProduct')}
              </button>
            </div>
            <div className={styles.filter}>
              <Select
                selectTexts={[
                  t('entrepeneurAccountProducts.higherPrices'),
                  t('entrepeneurAccountProducts.lowestPrices'),
                  t('entrepeneurAccountProducts.higherQuantities'),
                  t('entrepeneurAccountProducts.lowestQuantities'),
                ]}
                selectValues={['higherPrices', 'lowestPrices', 'higherQuantities', 'lowestQuantities']}
                selectedValue={filter}
                setSelectedValue={setFilter}
              />
            </div>
          </div>

          <div className={styles.productsContainer}>
            {products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())).map((product) => (
              isSmallScreen ? (
                <div key={product.id} className={styles.productContainerSmall}>
                  <div className={styles.buttonsContainerSmall}>
                    <button type="button" onClick={() => navigate(`/auth/entrepreneur/account/products/edit/${product.id}`)}><BsPencilFill /></button>
                    <button type="button" onClick={() => handleRemoveProduct(product.id)}><BsXLg /></button>
                  </div>
                  
                  <div className={styles.productContentSmall}>
                    <span>{t('entrepeneurAccountProducts.name')}</span>
                    <p className={styles.productDescription}>{product.name}</p>
                    <div className={styles.imageAndInfoContainer}>
                      <img className={styles.imageContainer} src={product.image} />
                      <section>
                        <div>
                          <span>{t('entrepeneurAccountProducts.category')}</span>
                          <p>{product.category}</p>
                        </div>       
                        <div>
                          <span>{t('entrepeneurAccountProducts.sold')}</span>
                          <p>{product.salesCount}</p>
                        </div>
                        <div>
                          <span>{t('entrepeneurAccountProducts.grade')}</span>
                          <p>
                            <BsStarFill />
                            {product.rating} (0 {t('entrepeneurAccountProducts.assessments')})
                          </p>
                        </div>         
                      </section>
                    </div>

                    <div className={styles.quantitiesContainerSmall}>
                      <div className={styles.quantitiesTitlesSmall}>
                        <span>{t('entrepeneurAccountProducts.unitPrice')}</span>
                        <span>{t('entrepeneurAccountProducts.inStock')}</span>
                      </div>
                      <div className={styles.quantitiesValuesSmall}>
                        <div>
                          <label>R$</label>
                          <input disabled type="text" defaultValue={product.originalPrice} />
                        </div>
                        <div className={styles.ajustValuesSmall}>
                          <label>Un</label>
                          <input disabled type="number" defaultValue={product.quantity} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={product.id} className={styles.productContainer}>
                  <div className={styles.productContent}>
                    <img 
                    className={styles.imageContainer ?? noImage} 
                    src={product.image} 
                    onError={(e) => {
                        e.currentTarget.onerror = null; 
                        e.currentTarget.src = noImage;
                    }} />

                    <div className={styles.detailsContainer}>
                      <span>{t('entrepeneurAccountProducts.name')}</span>
                      <p className={styles.productDescription}>{product.name}</p>
                      <span>{t('entrepeneurAccountProducts.category')}</span>
                      <p>{product.category}</p>
                      <div className={styles.soldContainer}>
                        <div>
                          <span>{t('entrepeneurAccountProducts.sold')}</span>
                          <p>{product.salesCount}</p>
                        </div>
                        <div>
                          <span>{t('entrepeneurAccountProducts.grade')}</span>
                          <p>
                            <BsStarFill />
                            {product.rating} (0 {t('entrepeneurAccountProducts.assessments')})
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.quantitiesContainer}>
                    <div className={styles.quantitiesTitles}>
                      <span>{t('entrepeneurAccountProducts.unitPrice')}</span>
                      <span>{t('entrepeneurAccountProducts.inStock')}</span>
                    </div>
                    <div className={styles.quantitiesValues}>
                      <div>
                        <label>R$</label>
                        <span>{product.originalPrice}</span>
                      </div>
                      <div className={styles.ajustValues}>
                        <label>Un</label>
                        <input disabled type="number" defaultValue={product.quantity} />
                      </div>
                    </div>
                  </div>

                  <div className={styles.buttonsContainer}>
                    <button type="button" onClick={() => navigate(`/auth/entrepreneur/account/products/edit/${product.id}`)}><BsPencilFill /></button>
                    <button type="button" onClick={() => handleRemoveProduct(product.id)}><BsXLg /></button>
                  </div>
                </div>
              )
            ))}
          </div>

          {selectedProduct && (
            <ModalExcluirItem
              isOpen={isModalOpen}
              handleExcluir={() => handleExcluir(selectedProduct.id)}
              handleCancelar={() => setIsModalOpen(false)}
              produto={selectedProduct}
            />
          )}
        </div>
      }
    </div>
  );
};

export default EntrepreneurAccountProducts;
